import { useState, useEffect } from "react";
import { Shield, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";

const STORAGE_KEY = "cc_privacy_accepted_v1";

export default function PrivacyBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(STORAGE_KEY);
    if (!accepted) {
      // Small delay so the banner doesn't flash before the page renders
      const t = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
      data-testid="privacy-banner"
    >
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 pointer-events-auto overflow-hidden animate-fade-in-up">
        <div className="p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center">
          <div className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-green-50 text-green-800">
            <Shield size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-base sm:text-lg text-slate-900 mb-1" data-testid="privacy-title">
              Informativa Privacy
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              I dati personali forniti tramite questo modulo sono trattati da{" "}
              <strong>UnionCasa Milano</strong> esclusivamente per emettere
              l&apos;Attestazione di Canone Concordato e gestire i relativi
              adempimenti, ai sensi del Reg. UE 2016/679 (GDPR).{" "}
              <PrivacyDetailsLink />
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={accept}
              className="text-slate-500 hover:text-slate-700 sm:hidden"
              data-testid="privacy-close-mobile"
              aria-label="Chiudi"
            >
              <X size={16} />
            </Button>
            <Button
              onClick={accept}
              className="bg-slate-900 hover:bg-slate-800 text-white w-full sm:w-auto"
              data-testid="privacy-accept"
            >
              Accetto e continuo
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrivacyDetailsLink() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-slate-900 underline underline-offset-2 font-medium hover:text-green-800 transition-colors"
          data-testid="privacy-details-trigger"
        >
          Leggi l&apos;informativa completa
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="privacy-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Informativa sul trattamento dei dati personali
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-700 leading-relaxed mt-4">
          <p>
            Ai sensi dell&apos;art. 13 del Reg. UE 2016/679 (GDPR), UnionCasa
            Milano in qualità di Titolare del trattamento informa che i dati
            personali raccolti tramite questo modulo saranno trattati per le
            finalità di seguito indicate.
          </p>

          <Section title="1. Titolare del trattamento">
            UnionCasa Milano — Associazione Nazionale a tutela della Proprietà
            Immobiliare.
          </Section>

          <Section title="2. Finalità e base giuridica">
            I dati conferiti (anagrafici di locatore e conduttore, dati
            dell&apos;immobile, estremi catastali, dati contrattuali, documenti
            allegati, firme) sono trattati al solo scopo di rilasciare
            l&apos;Attestazione di rispondenza del contratto di locazione
            all&apos;Accordo territoriale per il Comune di Milano del 12/06/2023
            (D.M. 16/01/2017). Base giuridica: esecuzione di obblighi
            contrattuali e adempimenti normativi.
          </Section>

          <Section title="3. Modalità di trattamento">
            Il trattamento avviene con strumenti informatici, in modo lecito,
            corretto e trasparente, con misure tecniche e organizzative
            adeguate a garantire sicurezza e riservatezza dei dati.
          </Section>

          <Section title="4. Categorie di dati trattati">
            Dati identificativi (nome, cognome, email), dati relativi
            all&apos;immobile (indirizzo, dati catastali, planimetria, APE),
            dati contrattuali (durata, canone), firme. Documenti caricati
            (visura, contratto, ricevute, copia documento e codice fiscale).
          </Section>

          <Section title="5. Destinatari">
            I dati potranno essere comunicati a: Organizzazioni firmatarie
            dell&apos;Accordo territoriale, professionisti incaricati,
            fornitori di servizi tecnici (hosting, invio email) limitatamente
            a quanto necessario per le finalità sopra indicate.
          </Section>

          <Section title="6. Conservazione">
            I dati saranno conservati per il tempo necessario alle finalità
            indicate e per gli obblighi di legge (almeno 10 anni per la
            documentazione contrattuale).
          </Section>

          <Section title="7. Diritti dell'interessato">
            Lei può in ogni momento esercitare i diritti di accesso, rettifica,
            cancellazione, limitazione, portabilità, opposizione e proporre
            reclamo al Garante Privacy. Per esercitare tali diritti può
            contattare il Titolare ai recapiti istituzionali di UnionCasa
            Milano.
          </Section>

          <Section title="8. Natura del conferimento">
            Il conferimento è facoltativo, ma necessario per il rilascio
            dell&apos;Attestazione. In caso di mancato conferimento, la
            richiesta non potrà essere processata.
          </Section>

          <p className="text-xs text-slate-500 italic pt-2 border-t border-slate-200">
            Proseguendo con la compilazione del modulo confermi di aver preso
            visione della presente informativa.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="font-semibold text-slate-900 mb-1">{title}</h4>
      <p>{children}</p>
    </div>
  );
}
